// Workflow assistant: the wire contract between the chat UI and the
// admin-assistant edge function.
//
// The backend half of these shapes lives in
// supabase/functions/_shared/workflow_model/copilot/ (feasibility.ts and
// narrative.ts). Kept in sync by hand; this file is the frontend's copy so the
// UI never imports Deno modules.

import type { BlockKind } from "./schema";

/**
 * The three answers to "can Dibs do this?". Decided server-side by diffing what
 * the model asked for against what survived sanitizeDraft(). Never asserted by
 * the model itself.
 */
export type CopilotVerdict = "feasible" | "partial" | "infeasible";

/**
 * Why a piece of the owner's request didn't make it into the plan.
 *
 * Mirrors the backend union in
 * _shared/workflow_model/copilot/feasibility.ts. Every SanitizeDrop kind plus
 * "reinterpreted" for what the model admits it couldn't express. Kept in sync
 * by hand until this file becomes a re-export of the shared module.
 */
export type CopilotGapKind =
  | "unsupported_trigger"
  | "unsupported_action"
  | "unsupported_field"
  | "unknown_node_type"
  | "incomplete_node"
  | "extra_trigger"
  | "unbound_ai_message"
  | "clamped"
  | "node_budget"
  | "no_trigger"
  | "too_few_nodes"
  | "reinterpreted";

export interface CopilotGap {
  kind: CopilotGapKind;
  /** The part of the request we couldn't honour, in the owner's own terms. */
  asked: string;
  /** Why not. Plain language, no engine jargon. */
  reason: string;
  /** What we did instead, when there was a near-enough substitute. */
  substitute?: string;
}

/** One row in the plan preview. Derived server-side from `toDisplaySteps()`. */
export interface CopilotPlanStep {
  kind: BlockKind;
  label: string;
  /** One line of "why this step is here". */
  detail?: string;
  /** Value chips shown under the label (channel, wait length, tag…). */
  params?: string[];
  /** Execution steps that hold for owner approval before they run. */
  requiresApproval?: boolean;
}

/**
 * The staged workflow, rendered for review. The graph itself stays server-side
 * in the copilot session until the owner builds it. The UI only ever sees this
 * narration, so it can't drift from what actually gets created.
 */
export interface CopilotPlan {
  name: string;
  description: string;
  steps: CopilotPlanStep[];
  /** Counted against MAX_NODES (30) so the panel can warn before the cap. */
  nodeCount: number;
}

export interface CopilotMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  /** Assistant turns only. */
  verdict?: CopilotVerdict;
  gaps?: CopilotGap[];
  plan?: CopilotPlan;
  /** A follow-up the owner can send with one click. */
  suggestion?: string;
  /** Set once this plan has been built. The card locks to its done state. */
  committedWorkflowId?: string;
}

export interface CopilotTurn {
  message: CopilotMessage;
}

/**
 * Everything the panel needs from the server. One interface so the mock and the
 * real edge-function client are interchangeable. See copilotTransport.ts.
 */
export interface CopilotTransport {
  startSession(businessId: string): Promise<{ sessionId: string }>;
  sendMessage(input: {
    sessionId: string;
    businessId: string;
    message: string;
  }): Promise<CopilotTurn>;
  commit(input: {
    sessionId: string;
    businessId: string;
  }): Promise<{ workflowId: string }>;
}

// Display helpers

interface VerdictCopy {
  /** Banner heading. Empty for `feasible`. A good plan needs no preamble. */
  title: string;
  tone: "ok" | "warn" | "stop";
}

export const VERDICT_COPY: Record<CopilotVerdict, VerdictCopy> = {
  feasible: { title: "", tone: "ok" },
  partial: { title: "Not all of that is possible", tone: "warn" },
  infeasible: { title: "Dibs can't do this yet", tone: "stop" },
};

/** Prompts for the empty state. Real workflows an owner would actually want. */
export const COPILOT_EXAMPLES: readonly string[] = [
  "Chase people who book a trial but never show up",
  "Ask for a review after someone has a great visit",
  "Win back customers who haven't booked in three months",
  "Warn me when a regular suddenly cancels",
];
